import dayjs from "dayjs";
import { toast } from "react-toastify";

import { handleUserMonitoring } from "@/lib/monitoring";
import { router } from "@/main";
import { AppDispatch } from "@/store/hooks";
import { authSliceActions } from "@/store/slices";
import { isLoginDriverResult, isLoginExpedResult } from "@/store/slices/auth/auth.guards";
import { startAppListening } from "@/store/utils";
import { isPayloadErrorWithMessage, isStatusCode409 } from "@/utils";

import { authApi } from "./auth.api";
import { LoginDriver, LoginExped } from "./auth.types";

const handleCreateSessionByPayload =
  ({ payload }: { payload: LoginDriver["result"] | LoginExped["result"] }) =>
  (dispatch: AppDispatch) => {
    if (isLoginDriverResult(payload)) {
      dispatch(authSliceActions.setDriverCredentials(payload));
    }

    if (isLoginExpedResult(payload)) {
      dispatch(authSliceActions.setExpeditionCredentials(payload));
    }
  };

//! Expedicão
startAppListening({
  matcher: authApi.endpoints.loginExped.matchFulfilled,
  effect: ({ payload }, { dispatch }) => {
    const { accessToken: token, role } = payload;

    handleUserMonitoring(payload);

    const makeSessionStorage = () => {
      sessionStorage.setItem("@App:id", String(payload.id));
      sessionStorage.setItem("@App:user", JSON.stringify(payload.user));
      sessionStorage.setItem("@App:token", token);
      sessionStorage.setItem("@App:userRole", payload.role);
    };

    if (payload.company) {
      localStorage.setItem("@App:companyId", String(payload.company?.id));
      sessionStorage.setItem("@App:companyName", payload.company?.name);
    }

    if (payload.shippingCompanyId) {
      localStorage.setItem("@App:shippingCompanyId", payload?.shippingCompanyId);
    }

    if (payload.role === "Transportadora") {
      if (payload.company) {
        sessionStorage.setItem("@App:temporaryCompanyId", String(payload.company.id));
      }
      sessionStorage.setItem("@App:temporaryId", String(payload.id));
    }
    makeSessionStorage();
    handleCreateSessionByPayload({ payload })(dispatch);

    const handleRedirectMatch = () => {
      const timestamp = dayjs().valueOf();
      sessionStorage.setItem("@App:timestamp", timestamp.toString());
      router.navigate({ to: "/expedicao" });
    };

    const matcher = new Map([
      ["Expedicao", handleRedirectMatch],
      ["Espectador", handleRedirectMatch],
      ["Portaria", handleRedirectMatch]
    ]);
    const handler = matcher.get(role);

    if (!handler) {
      toast.warning("Você não tem permissão de acesso!");
      return;
    }

    handler();
  }
});

startAppListening({
  matcher: authApi.endpoints.loginExped.matchRejected,
  effect: (action) => {
    if (action.error?.name === "AbortError") return;
    toast.error("Usuário ou Senha incorretos!");
  }
});

//! Motorista
startAppListening({
  matcher: authApi.endpoints.loginDriver.matchFulfilled,
  effect: (action, { dispatch }) => {
    const { payload } = action;
    handleCreateSessionByPayload({ payload })(dispatch);

    handleUserMonitoring(payload);

    const stringfy = <T>(value: T) => JSON.stringify(value);
    const keys = [
      { key: "@AppDriver:id", value: payload.id },
      { key: "@AppDriver:driver", value: payload.name },
      { key: "@AppDriver:companyId", value: payload.company?.id },
      { key: "@AppDriver:companyName", value: payload.company?.name }
    ];
    keys.forEach(({ key, value }) => sessionStorage.setItem(key, stringfy(value)));

    if (!payload.useTerms) {
      sessionStorage.setItem("@App:temporaryCompanyId", stringfy(payload.company?.id));
      sessionStorage.setItem("@App:temporaryDriverId", stringfy(payload.id));
    }
  }
});

startAppListening({
  matcher: authApi.endpoints.loginDriver.matchRejected,
  effect: ({ payload, error }) => {
    if (error?.name === "AbortError") return;

    if (isStatusCode409(payload) && isPayloadErrorWithMessage(payload)) {
      toast.error(payload.data.message);
      return;
    }

    if (isPayloadErrorWithMessage(payload)) {
      toast.error(payload.data.message);
      return;
    }

    toast.error("CPF ou Data de nascimento incorretos!");
  }
});
